<!--
/*******************************************************************************
*@ 컴포넌트명   : InputText.vue
*@ 컴포넌트설명 : input 공통컴포넌트
********************************************************************************
** 날짜/시      작성자   변경내역
********************************************************************************
** 2023.08.29   이가영  isKeepingLowerCase 옵션 추가
********************************************************************************/
-->
<template>
  <vuelidate-form
    :validator="isValidator ? $v[id] : null"
    :validator-params="{ ...errMsgParam, validatorParams }"
    :messages="errMsg"
  >
    <div
      :class="[cssHide ? 'form-text-field' : '', isValidator && ($v.$error || customError) ? 'error' : '']"
    >
      <label for="textfield1" class="form-label" v-if="label || $t(labelI18n)">
        {{ label || $t(labelI18n) }}
        <required v-if="isValidator && useRequired && (label || labelI18n)" />
      </label> 
      <div :class="['input-text-field', useRightIcon ? 'right-icon' : '' , phone ? 'phone' :'']">
        <input
          ref="input"
          :type="type"
          :class="['form-control']"
          :value="value"
          @input="inputEvent"
          :disabled="disabled"
          :placeholder="$t(placeholder)"
          @focus="onFocus"
          @blur="onBlur"
          @keydown.enter="onKeydownEnter"
        />
        <!-- 
          삭제버튼 노출 조건
          1) 금액 입력용 input 이고 value가 0 또는 ''이 아닐때 show
          2) input disabled가 아닐 때
          3) 고정길이 validation 적용이 아닐 때
          4) validation 적용이 있을 때
          4.1) 오류 상태가 아닐 때(validation/customer)
        -->
        <i
          class="icon icon-del"
          v-if="value != '' && value != '0' && isNumericComma && value != '' && !isNumericComma && !disabled && !isFixLength && (!isValidator || !$v.$error) && !customError"
          @click="initInputText"
        />
        <i class="icon icon-warning" v-if="isValidator && ($v.$error || customError)" />
        <i
          class="icon icon-success"
          v-if="value != '' && isValidator && isFixLength && !$v.$invalid"
        />
      </div>
    </div>
    <div v-if="customError" class="validate">
      <slot name="error" />
    </div>
  </vuelidate-form>
</template>
<script>
import { mapFields } from "vuex-map-fields";
import BaseContent from "@/components/BaseContent";
import { commonProps, valueProps, validation } from "@/mixins/basicComponent";
import { required, maxLength, minLength, alpha, numeric } from "vuelidate/lib/validators";
import {
  lengthByteCheck,
  alphaNum,
  alphaNumOnly,
  localeNum,
  numericComma,
  engCase1,
  engCase2,
  engCase3,
  engCase4,
  engCase5,
  engCase6,
  engCase7,
  engCase8,
  localeCase1,
  localeCase2,
  localeCase3,
  localeCase4,
  minValue,
  maxValue,
  cncycdDecpCheck,
  alphaLocaleOnly1,
  alphaLocaleOnly2,
  alphaLocaleOnly3,
} from "@/validators";

export default {
  name: "InputText",
  extends: BaseContent,
  mixins: [commonProps, valueProps, validation],
  props: {
    //validation check value
    id: {
      type: String,
      required: true,
    },
    //placeholder
    placeholder: {
      type: String,
      default: "",
    },
    customError: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: "text",
    },
    cssHide: {
      type: Boolean,
      default: true,
    },
    useRightIcon: {
      type: Boolean,
      default: true,
    },
    phone: {
      type: Boolean,
      default: false,
    },
    cncycd: String,
    isKeepingLowerCase: {
      type: Boolean,
      required: false,
      default: false,
    },
    onlyLowerCase: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  created() {
    // this.byteLength = this.$getByteLength(this.value);
    this[this.id] = this.value;
  },
  mounted() {},
  computed: {
    ...mapFields(["lang", "siteCode"]),
  },
  data() {
    return {
      [this.id]: "",
      // byteLength: 0, //입력된 텍스트 byte
      checkByte: 0, //최대 byte
      isLowerCase: false, //소문자 유지
      isFixLength: false, //고정길이 validation 적용 여부
      isNumericComma: false, //금액입력 적용 여부
      errMsg: {
        lengthByteCheck: "MDEP-02-01-13-24", //입력 가능한 최대 글자 수를 초과했습니다.
      },
      maxlengthValue: "",
    };
  },
  validations() {
    let customValidations = {};
    if (this.$attrs) {
      for (const key in this.$attrs) {
        let value = this.$attrs[key];
        let length = 0;
        let cncycdCheck = false;

        if (!this.isValidator) {
          this.isValidator = this.$config[`VALIDATOR_${key.toUpperCase()}`].includes(key);
        }
        this.isLowerCase = this.isKeepingLowerCase;
        //소문자 유지
        if (key == this.$config.VALIDATOR_TOLOWERCASE) {
          this.isLowerCase = true;
        }

        //value == "" : 모든 국가 적용
        if (value != "" || value.includes("|")) {
          if (!"string|number".includes(typeof value)) continue;
          let siteArr = typeof value == "string" ? value.split("|") : 0;
          if (
            `${this.$config.VALIDATOR_MAXLENGTH}|${this.$config.VALIDATOR_MINLENGTH}|
            ${this.$config.VALIDATOR_FIXLENGTH}|${this.$config.VALIDATOR_MAXBYTE}|
            ${this.$config.VALIDATOR_MINVALUE}|${this.$config.VALIDATOR_MAXVALUE}`.includes(key)
          ) {
            if (siteArr.length > 0) {
              for (const data of siteArr) {
                let con = data.split("=");
                if (con[0] == this.siteCode) length = Number(con[1]);
              }
              if (length == 0) continue;
            } else {
              length = value;
            }
          } else {
            let index = siteArr.indexOf(this.siteCode);
            if (index == -1) continue;
          }
        }

        //rule
        switch (key) {
          case this.$config.VALIDATOR_REQUIRED: //필수
            customValidations = {
              ...customValidations,
              required,
            };
            this.useRequired = true;
            break;
          case this.$config.VALIDATOR_MAXLENGTH: //최대 길이
            this.validatorParams = {
              ...this.validatorParams,
              max: value,
            };
            this.maxlengthValue = value;
            customValidations = {
              ...customValidations,
              maxLength: maxLength(length),
            };
            break;
          case this.$config.VALIDATOR_MINLENGTH: //최소 길이
            this.validatorParams = {
              ...this.validatorParams,
              min: value,
            };
            customValidations = {
              ...customValidations,
              minLength: minLength(length),
            };
            break;
          case this.$config.VALIDATOR_FIXLENGTH: //고정 길이
            this.validatorParams = {
              ...this.validatorParams,
              min: value,
              max: value,
            };
            customValidations = {
              ...customValidations,
              minLength: minLength(length),
              maxLength: maxLength(length),
            };
            this.isFixLength = true;
            break;
          case this.$config.VALIDATOR_MAXBYTE: //최대 byte
            this.checkByte = value;
            customValidations = {
              ...customValidations,
              lengthByteCheck: lengthByteCheck({ text: this[this.id], maxLength: length }),
            };
            break;
          case this.$config.VALIDATOR_MINVALUE: //최소값
            this.validatorParams = {
              ...this.validatorParams,
              minValue: value,
            };
            customValidations = {
              ...customValidations,
              minValue: minValue(length),
            };

            break;
          case this.$config.VALIDATOR_MAXVALUE: //최대값
            this.validatorParams = {
              ...this.validatorParams,
              maxValue: value,
            };
            customValidations = {
              ...customValidations,
              maxValue: maxValue(length),
            };
            break;
          case this.$config.VALIDATOR_ALPHA: //영어
            customValidations = {
              ...customValidations,
              alpha,
            };
            break;
          case this.$config.VALIDATOR_ALPHANUM: //영어, 숫자, 띄어쓰기
            customValidations = {
              ...customValidations,
              alphaNum,
            };
            break;
          case this.$config.VALIDATOR_ALPHANUM_ONLY: //영어, 숫자만
            customValidations = {
              ...customValidations,
              alphaNumOnly,
            };
            break;
          case this.$config.VALIDATOR_LOCALENUM: //한국어, 외국어, 숫자, 띄어쓰기
            customValidations = {
              ...customValidations,
              localeNum,
            };
            break;
          case this.$config.VALIDATOR_NUMERIC: //숫자
            customValidations = {
              ...customValidations,
              numeric,
            };
            break;
          case this.$config.VALIDATOR_NUMERICCOMMA: //숫자, 특수문자 ,
            this.isNumericComma = true;

            switch (this.cncycd) {
              case this.$config.CNCYCD_ITL:
              case this.$config.CNCYCD_ESP:
              case this.$config.CNCYCD_JPY:
              case this.$config.CNCYCD_KRW:
                cncycdCheck = true;
                break;
            }

            if (cncycdCheck) {
              customValidations = {
                ...customValidations,
                cncycdDecpCheck: cncycdDecpCheck(this.cncycd),
              };
            } else {
              customValidations = {
                ...customValidations,
                numericComma,
              };
            }

            break;
          case this.$config.VALIDATOR_ENGCASE1: //영어, 숫자, 띄어쓰기, 특수문자 . - & ( ) / %
            customValidations = {
              ...customValidations,
              engCase1,
            };
            break;
          case this.$config.VALIDATOR_ENGCASE2: //영어, 숫자, 띄어쓰기, 특수문자   -
            customValidations = {
              ...customValidations,
              engCase2,
            };
            break;
          case this.$config.VALIDATOR_ENGCASE3: //영어, 숫자, 띄어쓰기, 특수문자 . - & ( ) /
            customValidations = {
              ...customValidations,
              engCase3,
            };
            break;
          case this.$config.VALIDATOR_ENGCASE4: //영어, 숫자, 띄어쓰기, 특수문자 . -   ( ) /   , : +
            customValidations = {
              ...customValidations,
              engCase4,
            };
            break;
          case this.$config.VALIDATOR_ENGCASE5: //영어, 숫자, 띄어쓰기, 특수문자 . - & ( ) /   , :
            customValidations = {
              ...customValidations,
              engCase5,
            };
            break;
          case this.$config.VALIDATOR_ENGCASE6: //영어, 숫자, 띄어쓰기, 특수문자 . - & ( ) /   , : + ? ' { }
            customValidations = {
              ...customValidations,
              engCase6,
            };
            break;
          case this.$config.VALIDATOR_ENGCASE7: //영어, 숫자, 띄어쓰기, 특수문자 , - ( )
            customValidations = {
              ...customValidations,
              engCase7,
            };
            break;
          case this.$config.VALIDATOR_ENGCASE8: //영어, 숫자, 띄어쓰기, (키보드에 있는)특수문자
            customValidations = {
              ...customValidations,
              engCase8,
            };
            break;
          case this.$config.VALIDATOR_LOCALECASE1: //한국어, 외국어, 숫자, 특수문자 . - & ( ) / %
            customValidations = {
              ...customValidations,
              localeCase1,
            };
            break;
          case this.$config.VALIDATOR_LOCALECASE2: //한국어, 외국어, 숫자, 띄어쓰기, 특수문자   -
            customValidations = {
              ...customValidations,
              localeCase2,
            };
            break;
          case this.$config.VALIDATOR_LOCALECASE3: //한국어, 외국어, 숫자, 띄어쓰기, 특수문자 . - & ( ) /
            customValidations = {
              ...customValidations,
              localeCase3,
            };
            break;
          case this.$config.VALIDATOR_LOCALECASE4: //한국어, 외국어, 숫자, 띄어쓰기, 특수문자 . -   ( ) /   , : +
            customValidations = {
              ...customValidations,
              localeCase4,
            };
            break;
          case this.$config.VALIDATOR_ALPHLOCALE_ONLY1: //외국어, 숫자, 특수문자 . - & ( ) / %
            customValidations = {
              ...customValidations,
              alphaLocaleOnly1,
            };
            break;
          case this.$config.VALIDATOR_ALPHLOCALE_ONLY2: //외국어, 숫자
            customValidations = {
              ...customValidations,
              alphaLocaleOnly2,
            };
            break;
          case this.$config.VALIDATOR_ALPHLOCALE_ONLY3: //영문, 숫자, 띄어쓰기, 특수문자 - /
            customValidations = {
              ...customValidations,
              alphaLocaleOnly3,
            };
            break;
        }
      }
    }

    return {
      [this.id]: customValidations,
    };
  },
  methods: {
    /**
     * text를 통화형식으로 format
     * @param {String} val 변경할 텍스트
     * @returns {String} ##,###
     */
    comma(val) {
      let numValue = "";
      const isDot = val.indexOf(".") > -1;
      if (isDot) {
        numValue += val.split(".")[0];
      } else {
        numValue += val;
      }
      numValue = numValue.replace(/,/gi, "");
      var returnVal = "";
      for (var i = numValue.length - 1; i >= 0; i--) {
        returnVal = numValue.charAt(i) + returnVal;
        if (i != 0 && i % 3 == numValue.length % 3) returnVal = "," + returnVal;
      }

      if (Number(returnVal) >= 0) {
        returnVal = Number(returnVal);
      }

      return isDot ? `${returnVal}.${val.split(".")[1]}` : returnVal.toString();
    },

    /**
     * 입력 input event
     * @param {Object} e event
     */
    inputEvent(e) {
      let inputData = e.target.value;

      //소문자 유지
      if (!this.isLowerCase && !this.isKeepingLowerCase) {
        inputData = inputData.toUpperCase();
      }

      if (this.onlyLowerCase) {
        //소문자만 입력
        inputData = inputData.toLowerCase();
      }

      // this.byteLength = this.$getByteLength(e.target.value);
      //숫자 콤마
      if (this.$v[this.id][this.$config.VALIDATOR_NUMERICCOMMA]) {
        inputData = this.comma(inputData);
        this.$forceUpdate();
      }

      this[this.id] = inputData;
      if (this.isValidator) {
        this.$v[this.id].$touch();
      }
      this.$emit("inputTextEvent", this.$v.$invalid);
      this.$emit("update:value", inputData);
    },
    /**
     * 입력 input init
     * @param {Object} e Event
     */
    initInputText(e) {
      this[this.id] = "";
      this.$emit("update:value", "");
      if (this.isValidator) {
        this.$v[this.id].$reset();
      }

      //x버튼으로 초기화 했을 경우 포커스 유지
      if (!_.isNil(e) && e.type == "click") {
        this.$refs.input.focus();
      }
    },
    /**
     * set text
     * @param {String} str set text
     */
    setText(str) {
      this.inputEvent({ target: { value: str } });
    },
    /**
     * input focus event
     */
    onFocus() {
      this.$emit("focus");
    },
    onBlur() {
      this.$emit("blur");
    },
    onKeydownEnter() {
      this.$emit("keydownEnter");
    }
  },
};
</script>
